import React, { useEffect, useState } from 'react';
import { MailIcon, MailCheckIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { PinInput } from './PinInput';
import { useNotes } from '../../contexts/NotesContext';
import { challengeExpired, challengeMinutesLeft, maskEmail, pinProblem } from '../../utils/notesLock';
import { account } from '../../data/account';

/**
 * Forgotten-PIN recovery. Ownership of the account email is verified with an
 * expiring code before a new PIN can be chosen. The old PIN is never revealed,
 * because only its hash was ever stored.
 */
export function PinResetDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    resetChallenge,
    sendResetCode,
    verifyResetCode,
    resetChallengeVerified,
    resetPin,
    cancelReset
  } = useNotes();
  const [code, setCode] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setCode('');
      setNext('');
      setConfirm('');
      setError(null);
      if (!resetChallenge) sendResetCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  function close() {
    cancelReset();
    onClose();
  }
  const expired = resetChallenge ? challengeExpired(resetChallenge) : false;
  const stage = resetChallengeVerified ? 'choose' : 'code';
  return <Modal open={open} onClose={close} title="Reset your Notes PIN" description={`We have emailed a six-digit code to ${maskEmail(account.email)}.`} footer={<>
          <Button variant="ghost" onClick={close} data-id="element-3903" data-mp="3dmg1eygpn0cl">
            Cancel
          </Button>
          {stage === 'code' ? <Button variant="primary" disabled={code.length !== 6 || expired} onClick={() => {
      if (verifyResetCode(code)) setError(null);else setError('That code is not correct. Check the most recent email.');
    }} data-id="element-3904" data-mp="56d33jbhmo0x">
              Verify code
            </Button> : <Button variant="primary" onClick={() => {
      const problem = pinProblem(next);
      if (problem) {
        setError(problem);
        return;
      }
      if (next !== confirm) {
        setError('The two PINs do not match.');
        return;
      }
      resetPin(next);
      onClose();
    }} data-id="element-3905" data-mp="9v5ockhi9q4z">
              Set new PIN
            </Button>}
        </>} data-id="element-3902" data-mp="3nc99vqlk2zcm">
      <div className="space-y-3.5" data-id="element-3906" data-mp="3ia60g50coo72">
        {stage === 'code' ? <>
            <div className="flex items-start gap-2.5 rounded-lg border border-line bg-canvas px-3.5 py-3" data-id="element-3907" data-mp="3echy4gwbvt55">
              <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-3908" data-mp="39dol0pkye45i" />
              <div className="min-w-0" data-id="element-3909" data-mp="1ot09wx02ri0h">
                <p className="text-[13px] leading-6 text-ink" data-id="element-3910" data-mp="1ts4ipmj37dqa">
                  Enter the code from the email sent to{' '}
                  <span className="font-medium" data-id="element-3911" data-mp="1xv157obqztzi">{maskEmail(account.email)}</span>.
                </p>
                {resetChallenge && !expired && <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-3912" data-mp="22qovk71yut6l">
                    Expires in {challengeMinutesLeft(resetChallenge)} minute
                    {challengeMinutesLeft(resetChallenge) === 1 ? '' : 's'}.
                  </p>}
                {/* Demo only: a real deployment never returns the code to the client. */}
                {resetChallenge && !expired && <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px] text-ink-muted" data-id="element-3913" data-mp="mesx77djrqgg">
                    <Badge tone="neutral" data-id="element-3914" data-mp="rdmdt8o4rxku">Preview build</Badge>
                    <span data-id="element-3915" data-mp="rc2ybkaatqk4">
                      the code is{' '}
                      <span className="cz-num font-semibold text-ink" data-id="element-3916" data-mp="2u1mddghfecy5">{resetChallenge.code}</span>; in
                      production it only reaches your inbox.
                    </span>
                  </p>}
              </div>
            </div>

            <div data-id="element-3917" data-mp="2y26a4iy37ejr">
              <label className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3918" data-mp="2y1mpguh1yc21">Six-digit code</label>
              <input inputMode="numeric" autoFocus value={code} maxLength={6} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} aria-label="Reset code" aria-invalid={error !== null} className={`cz-num h-11 w-full rounded-lg border bg-surface px-3 text-center text-lg tracking-[0.4em] text-ink focus:outline-none ${error ? 'border-danger-700' : 'border-line-strong focus:border-brand-700'}`} data-id="element-3919" data-mp="3cqj45adm6nbm" />
            </div>

            <div className="flex flex-wrap items-center gap-3" data-id="element-3920" data-mp="1d9h4n7k2zvi7">
              <Button size="sm" variant="secondary" onClick={sendResetCode} data-id="element-3921" data-mp="1h9j7hk1jyuz2">
                Send a new code
              </Button>
              {resetChallenge && resetChallenge.attempts > 0 && <span className="text-[12px] text-warn-700" data-id="element-3922" data-mp="18h7k671qjnnb">
                  {resetChallenge.attempts} incorrect attempt
                  {resetChallenge.attempts === 1 ? '' : 's'}.
                </span>}
            </div>

            {expired && <p className="rounded-lg border border-warn-100 bg-warn-50 px-3.5 py-2.5 text-[12px] leading-5 text-ink" data-id="element-3923" data-mp="yh6w67sx8jcs">
                That code has expired. Send a new one to continue.
              </p>}
          </> : <>
            <p className="flex items-start gap-2.5 rounded-lg border border-ok-100 bg-ok-50 px-3.5 py-3 text-[13px] leading-6 text-ink" data-id="element-3924" data-mp="2eykxooslwe6f">
              <MailCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-3925" data-mp="2jujosk5bkaaj" />
              Email verified. Choose a new PIN — your locked notes are untouched and will open with it.
            </p>
            <div data-id="element-3926" data-mp="2digwtn55zk9p">
              <label className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3927" data-mp="28h9nvkracf1g">New PIN</label>
              <PinInput value={next} onChange={setNext} label="New PIN" autoFocus invalid={error !== null} data-id="element-3928" data-mp="23i6g3sjx7ups" />
              <p className="mt-1.5 text-[12px] text-ink-muted" data-id="element-3929" data-mp="315ozryf4eh8k">
                4 to 6 digits. Not all the same digit, and not in sequence.
              </p>
            </div>
            <div data-id="element-3930" data-mp="2w8yij7rkxflk">
              <label className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-3931" data-mp="2rg5fcobrefbl">Confirm PIN</label>
              <PinInput value={confirm} onChange={setConfirm} label="Confirm new PIN" invalid={error !== null} data-id="element-3932" data-mp="2mh28cyc7xz0z" />
            </div>
          </>}

        {error && <p className="rounded-lg border border-danger-100 bg-danger-50 px-3.5 py-2.5 text-[12px] leading-5 text-danger-700" data-id="element-3933" data-mp="3jyj500mxyuzl">
            {error}
          </p>}
      </div>
    </Modal>;
}